import React, { useState, useRef, useEffect } from 'react';
import { X, Shield, Check, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import otpCallbackRegistry from '../utils/otpCallbackRegistry';
import notificationApiService from '../services/notificationApiService';
import notificationService from '../services/notificationService';

const OTPActionModal = ({ 
  isOpen, 
  onClose, 
  notificationData 
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef([]);

  // Auto-fill OTP when modal opens
  useEffect(() => {
    if (isOpen && notificationData?.metadata?.expectedOtp) {
      const otpString = notificationData.metadata.expectedOtp.toString();
      const otpArray = otpString.split('').slice(0, 6);
      // Pad with empty strings if less than 6 digits
      while (otpArray.length < 6) {
        otpArray.push('');
      }
      setOtp(otpArray);
      
      // Focus on the last filled input or first empty one
      setTimeout(() => {
        const firstEmptyIndex = otpArray.findIndex(digit => digit === '');
        const focusIndex = firstEmptyIndex === -1 ? 5 : firstEmptyIndex;
        inputRefs.current[focusIndex]?.focus();
      }, 100);
    } else if (!isOpen) {
      // Reset OTP when modal closes
      setOtp(['', '', '', '', '', '']);
    }
  }, [isOpen, notificationData]);

  if (!isOpen || !notificationData) return null;

  const { metadata } = notificationData;
  const { expectedOtp, items = [], actionType } = metadata || {};

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = pastedData.split('');
    setOtp([...newOtp, ...Array(6 - newOtp.length).fill('')]);
    
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleVerify = async () => {
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }

    if (otpString !== expectedOtp) {
      toast.error('Invalid OTP code');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      return;
    }

    setIsVerifying(true);
    
    try {
      // Get and execute the callback from the registry
      console.log('🔐 Looking for callback with OTP:', expectedOtp);
      const callbackEntry = otpCallbackRegistry.get(expectedOtp);
      console.log('🔐 Callback entry found:', callbackEntry);
      
      if (callbackEntry && callbackEntry.callback) {
        console.log('🔐 Executing callback...');
        
        try {
          // Execute the main action (delete item, clear cart, etc.)
          // The callback functions will handle updating the notification status
          await callbackEntry.callback();
          console.log('✅ Callback executed successfully');
          
          // Clean up the callback after use
          otpCallbackRegistry.remove(expectedOtp);
          toast.success('Action confirmed successfully!');
          onClose();
          
        } catch (callbackError) {
          console.error('❌ Error executing callback:', callbackError);
          throw callbackError; // Re-throw to be caught by outer catch block
        }
      } else {
        console.log('🔐 All registered callbacks:', otpCallbackRegistry.getAllCallbacks());
        toast.error('Action callback not found. Please try again from the main modal.');
        onClose();
      }
    } catch (error) {
      console.error('Error confirming action:', error);
      
      // Check if the error is from the callback execution or notification marking
      if (error.message && error.message.includes('notification')) {
        toast.error('Action completed but failed to update notification status');
      } else {
        toast.error('Failed to complete action: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const getActionTypeLabel = () => {
    switch (actionType) {
      case 'remove_cart_item':
        return 'Remove Cart Item';
      case 'clear_cart':
        return 'Clear Shopping Cart';
      case 'delete_held_sale':
        return 'Delete Held Sale';
      default:
        return 'Confirm Action';
    }
  };

  const otpString = otp.join('');
  const isComplete = otpString.length === 6;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Shield className="text-blue-600" size={24} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">{getActionTypeLabel()}</h3>
              <p className="text-sm text-gray-500">Verification Required</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        {/* Items List */}
        {items && items.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Items to be affected:</h4>
            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
              {items.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{item.name || item.productName}</p>
                    {item.quantity && (
                      <p className="text-sm text-gray-500">
                        Qty: {item.quantity} {item.uom ? `× ${item.uom}` : ''}
                      </p>
                    )}
                  </div>
                  {item.amount && (
                    <div className="text-right">
                      <p className="font-semibold text-gray-800">₵{parseFloat(item.amount).toFixed(2)}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Total if available */}
            {items.length > 1 && items[0].amount && (
              <div className="mt-3 flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                <span className="font-semibold text-gray-700">Total Amount:</span>
                <span className="text-xl font-bold text-blue-600">
                  ₵{items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* OTP Input Section */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg mb-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-medium text-gray-800 mb-1">
                  Security Verification Required
                </p>
                <p className="text-xs text-gray-600">
                  Enter the 6-digit code from your notifications to confirm this action
                </p>
              </div>
            </div>
          </div>

          <label className="block text-sm font-medium text-gray-700 mb-3">
            Enter Verification Code
          </label>
          
          <div className="flex justify-center gap-2 mb-4">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={isVerifying}
                className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl transition-all duration-200 focus:outline-none ${
                  digit
                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-gray-300 bg-white text-gray-800'
                } ${
                  isVerifying ? 'opacity-50 cursor-not-allowed' : 'focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={isVerifying}
            className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleVerify}
            disabled={!isComplete || isVerifying}
            className={`flex-1 px-4 py-3 rounded-xl font-semibold text-white transition-all duration-200 ${
              !isComplete || isVerifying
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl'
            }`}
          >
            {isVerifying ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Verifying...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <Check size={20} />
                <span>Verify & Confirm</span>
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPActionModal;

